import { getToday } from "../utils";

// ═══════════════════════════════════════════════════════════════
// CONFERÊNCIA DO GERENTE — o comercial executa, o gerente avalia
// ═══════════════════════════════════════════════════════════════
//
// O comercial registra o ato (contato, aceite, recusa, justificativa). O
// gerente confere o que foi registrado e avalia a qualidade — que é a razão
// de a trilha existir: sem alguém lendo, ela vira arquivo morto.
//
// A avaliação mora DENTRO do histórico (stage_history), como todo o resto:
// coluna nova é campo que a gravação descarta sem avisar. Ela aponta para o
// evento avaliado por `ref`, uma chave derivada do próprio evento — os
// registros antigos não têm id, e reescrevê-los para criar um seria mexer no
// que já aconteceu.

export const QUALIDADES = [
  { id: "bom", label: "Bem feito", cor: "#059669", dica: "Contato completo, com prova e próximo passo claro" },
  { id: "ok", label: "Aceitável", cor: "#d97706", dica: "Serve, mas dava para registrar melhor" },
  { id: "fraco", label: "Precisa melhorar", cor: "#dc2626", dica: "Faltou informação, prova ou próximo passo" },
];

export const rotuloQualidade = (id) => QUALIDADES.find((q) => q.id === id)?.label || id || "—";
export const corQualidade = (id) => QUALIDADES.find((q) => q.id === id)?.cor || "#64748b";

export const VEREDITOS = {
  confirmado: { label: "Confirmado", cor: "#059669" },
  refazer: { label: "Refazer", cor: "#dc2626" },
};

/** Chave estável de um evento — serve para eventos antigos, que não têm id. */
export const chaveEvento = (ev = {}, origem = "trilha", i = 0) =>
  ev.id || `${origem}:${i}:${ev.date || ev.ts || ""}:${ev.to || ev.channel || ev.tipo || ""}`;

/**
 * Os atos do comercial que valem conferência.
 *
 * Contato registrado, aceite, recusa e justificativa de espera — é onde a
 * qualidade do trabalho aparece. Mudança de estágio automática e a própria
 * avaliação ficam de fora: conferir o que o sistema fez sozinho, ou conferir
 * a conferência, só geraria fila sem ninguém aprender nada.
 */
export function eventosConferiveis(parceiro = {}) {
  const eventos = [];

  (Array.isArray(parceiro.interactions) ? parceiro.interactions : []).forEach((it, i) => {
    eventos.push({
      ref: chaveEvento(it, "contato", i),
      tipo: "contato",
      data: it.date || "",
      titulo: `Contato — ${it.channel || "registro"}`,
      detalhe: it.note || "",
      autor: it.autor || "",
      comprovante: it.comprovantePath || "",
      contatoEm: it.contatoEm || "",
      analiseIa: it.analiseIa || null,
      uf: it.uf || "", cidade: it.cidade || "",
      // Quando a conversa aconteceu x quando foi registrada. A meta conta pelo
      // registro (é o dia de trabalho medido), mas a distância entre os dois é
      // informação de gestão: lote inteiro lançado dias depois muda o que o
      // número do dia significa.
      diaDaConversa: it.diaDaConversa || "",
      diasDeAtraso: Number(it.diasDeAtraso || 0),
    });
  });

  (Array.isArray(parceiro.stageHistory) ? parceiro.stageHistory : []).forEach((h, i) => {
    if (!["sem_interesse", "justificativa"].includes(h?.tipo) && h?.to !== "documentacao") return;
    eventos.push({
      ref: chaveEvento(h, "trilha", i),
      tipo: h.tipo === "justificativa" ? "justificativa" : h.tipo === "sem_interesse" ? "recusa" : "aceite",
      data: h.date || "",
      titulo: h.tipo === "justificativa"
        ? "Justificou a espera"
        : h.tipo === "sem_interesse" ? "Marcou sem interesse" : "Registrou o aceite",
      detalhe: h.note || h.motivo || "",
      autor: h.autor || "",
    });
  });

  return eventos.sort((a, b) => (b.data || "").localeCompare(a.data || ""));
}

/** As avaliações já dadas, indexadas pelo evento que avaliaram. */
export function avaliacoes(parceiro = {}) {
  const mapa = {};
  (Array.isArray(parceiro.stageHistory) ? parceiro.stageHistory : [])
    .filter((h) => h?.tipo === "avaliacao" && h?.ref)
    .forEach((h) => { mapa[h.ref] = h; });
  return mapa;
}

/** O que o gerente ainda não olhou. */
export function pendentesDeConferencia(parceiro = {}) {
  const feitas = avaliacoes(parceiro);
  return eventosConferiveis(parceiro).filter((ev) => !feitas[ev.ref]);
}

export const temPendencia = (parceiro) => pendentesDeConferencia(parceiro).length > 0;

/** O último pedido de refazer que ainda não foi respondido por um ato novo. */
export function correcaoAberta(parceiro = {}) {
  const avaliacoesRefazer = (Array.isArray(parceiro.stageHistory) ? parceiro.stageHistory : [])
    .filter((h) => h?.tipo === "avaliacao" && h?.veredito === "refazer");
  const ultima = avaliacoesRefazer[avaliacoesRefazer.length - 1];
  if (!ultima) return null;
  // Ato registrado depois do pedido já é a resposta a ele.
  if (parceiro.lastContact && parceiro.lastContact > ultima.date) return null;
  return ultima;
}

/**
 * Registra a avaliação de um evento. Não mexe no estágio: o gerente avalia
 * como o trabalho foi feito, não decide por onde a empresa anda — isso
 * continua sendo consequência do que foi registrado.
 */
export function aplicarAvaliacao(parceiro, { ref, veredito, qualidade, comentario = "", autor = "" }, hoje = getToday()) {
  return {
    ...parceiro,
    stageHistory: [
      ...(Array.isArray(parceiro.stageHistory) ? parceiro.stageHistory : []),
      {
        from: parceiro.stage, to: parceiro.stage, date: hoje, autor,
        tipo: "avaliacao", ref, veredito, qualidade, comentario: comentario.trim(),
        note: `${VEREDITOS[veredito]?.label || veredito} pelo gerente — ${rotuloQualidade(qualidade)}${comentario.trim() ? `: ${comentario.trim()}` : ""}`,
      },
    ],
  };
}

/** Como anda a qualidade desta empresa — o que a trilha existe para mostrar. */
export function resumoQualidade(parceiro = {}) {
  const lista = Object.values(avaliacoes(parceiro));
  const conta = { bom: 0, ok: 0, fraco: 0 };
  lista.forEach((a) => { if (conta[a.qualidade] !== undefined) conta[a.qualidade] += 1; });
  return { total: lista.length, ...conta, refazer: lista.filter((a) => a.veredito === "refazer").length };
}
